import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Target } from 'lucide-react';
import { TennisBall } from '@/assets/icons/TennisBall';
import type { SegmentStatistics } from '@/types/statistics';
import { largestRemainderPercents, REPORT_SKIN } from '@/lib/utils';
import { usePlayerNameResolver } from '@/contexts/PlayerNamesContext';
import type { PlayerColor } from './playerColors';
import { GaugeArc } from './Donut';
import { EntityAvatar } from './EntityAvatar';
import { ConsistencyDiverging, type ConsistencyDatum } from './StyleOfPlay';
import CardHead from './CardHead';
import {
  playerConsistency,
  playerShotTotal,
  resolveTeams,
} from './tournamentMetrics';
import { pointLossBreakdown } from './contributionMetrics';
import { ShotRaces } from './ContributionIndexCard';

// The Visão Geral "Contribution" card: how the viewer contributed to the
// scoreboard — their consistency (in-play vs error), everyone's error rate,
// winner rate and winners per error over the block, a breakdown of HOW they lost points
// (net vs wall errors), and how shots were distributed between the two players
// of each team (your team vs the opponents).
interface ContributionCardProps {
  segment: SegmentStatistics;
  crops: Record<string, string>;
  colors: Record<string, PlayerColor>;
  /** The player the card is scoped to (the identified viewer). */
  focusPlayerId: string;
  /** The block the consistency bars and the error / winner charts are drawn
      from (`segment` by default; the locked preview's sample). */
  shotSegment?: SegmentStatistics;
}

// Error-breakdown gauge colors — kept in the report's sky/navy palette (no warm
// hues) so the gauge sits with the rest of the theme; the heading + legend carry
// the "errors / lost points" meaning. Black and gray in the Matchlytics skin.
const LOSS_COLORS =
  REPORT_SKIN === 'matchlytics'
    ? { net: '#0a0a0a', wall: '#a3a3a3' }
    : {
        net: '#0ea5e9', // sky — into the net
        wall: '#0c4a6e', // deep navy — off the back wall (out)
      };

const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="eyebrow">
    <span className="eyebrow-pt">{children}</span>
  </div>
);

// A single proportional bar splitting one team's shots between its two players,
// each in their identity color, names above and shot counts below.
interface SplitPlayer {
  name: string;
  color: string;
  shots: number;
  crop?: string;
}

const ShotSplitBar: React.FC<{
  sectionLabel: string;
  left: SplitPlayer;
  right: SplitPlayer;
}> = ({ sectionLabel, left, right }) => {
  const [lPct, rPct] = largestRemainderPercents([left.shots, right.shots]);
  return (
    <div className="csplit">
      <div className="csplit-label">{sectionLabel}</div>
      <div className="csplit-head">
        <span className="csplit-side">
          <EntityAvatar name={left.name} color={left.color} crop={left.crop} />
          <span className="csplit-name">{left.name}</span>
        </span>
        <span className="csplit-side csplit-side-right">
          <span className="csplit-name">{right.name}</span>
          <EntityAvatar
            name={right.name}
            color={right.color}
            crop={right.crop}
          />
        </span>
      </div>
      {/* Two-tone segments: the left player in accent blue, the right in the
          deeper navy — the colored names above and percentages below identify
          each, so the bar shows only the split; the 2px gap keeps them apart. */}
      <div className="csplit-track">
        {lPct > 0 ? (
          <span
            className="csplit-fill"
            style={{ width: `${lPct}%`, background: 'var(--accent)' }}
          />
        ) : null}
        {rPct > 0 ? (
          <span
            className="csplit-fill"
            style={{ width: `${rPct}%`, background: 'var(--accent-deep)' }}
          />
        ) : null}
      </div>
      {/* Each player's share of their OWN team's shots (the pair sums to 100%). */}
      <div className="csplit-foot">
        <span>{lPct}%</span>
        <span>{rPct}%</span>
      </div>
    </div>
  );
};

const ContributionCard: React.FC<ContributionCardProps> = ({
  segment,
  crops,
  colors,
  focusPlayerId,
  shotSegment = segment,
}) => {
  const { t } = useTranslation('tournament');
  const tl = (k: string) => t(`label.${k}` as never) as string;
  const resolveName = usePlayerNameResolver();
  const stats = segment.statistics;
  const ps = stats.players_statistics[focusPlayerId];
  // Every section is shot-dependent — hide the whole card without shot tracking.
  if (!ps?.shots_tracking) return null;

  // 1. Consistency — in-play vs error (same graph as the standalone card, sans
  //    the "more consistent / more errors" footer).
  const cons = playerConsistency(
    shotSegment.statistics.players_statistics[focusPlayerId] ?? ps,
  );
  const name = resolveName(focusPlayerId);
  const color = colors[focusPlayerId]?.solid ?? 'var(--accent)';
  const consData: ConsistencyDatum[] = cons
    ? [{ id: focusPlayerId, name, color, crop: crops[focusPlayerId], ...cons }]
    : [];

  // 2. How points were lost — net vs wall errors.
  const loss = pointLossBreakdown(ps);
  const lossPcts = loss
    ? largestRemainderPercents([loss.net, loss.wall])
    : null;

  // 3. Shot distribution within each team. Order the opponents by role (left
  //    first) so the bar reads left-player → right-player.
  const roster = resolveTeams(stats.teams, focusPlayerId);
  const shotsOf = (id: string) => {
    const p = stats.players_statistics[id];
    return p ? playerShotTotal(p) : 0;
  };
  // Both bars read left-player → right-player, so the two rows line up as
  // court geometry rather than one being you-first and the other side-first.
  const byRole = (ids: string[]) =>
    [...ids].sort((a, b) => {
      const ra = stats.roles[a]?.primary_role;
      const rb = stats.roles[b]?.primary_role;
      if (ra === 'left' && rb !== 'left') return -1;
      if (rb === 'left' && ra !== 'left') return 1;
      return 0;
    });
  const yourByRole = roster ? byRole(roster.yourMembers) : [];
  const oppByRole = roster ? byRole(roster.oppMembers) : [];
  const player = (id: string): SplitPlayer => ({
    name: resolveName(id),
    color: colors[id]?.solid ?? 'var(--muted-2)',
    shots: shotsOf(id),
    crop: crops[id],
  });
  const teamShots = (ids: string[]) =>
    ids.reduce((sum, id) => sum + shotsOf(id), 0);
  const showDistribution =
    yourByRole.length === 2 &&
    oppByRole.length === 2 &&
    teamShots(yourByRole) + teamShots(oppByRole) > 0;

  // 4. Error rate, winner rate and winners per error over the block, all four
  //    players in the header's order (your team first, each pair left to
  //    right).
  const racePlayers = roster
    ? [...yourByRole, ...oppByRole]
    : Object.keys(stats.players_statistics);

  return (
    <div className="card contribution-card">
      <CardHead
        icon={Target}
        title={t('overview.contribution.title')}
        subtitle={t('overview.contribution.subtitle')}
      />

      {/* Consistency of the player's shots (in play vs error) — the label +
          ball icon make clear these percentages are about shots. */}
      {consData.length ? (
        <div className="contribution-block">
          <div className="contribution-caption-row">
            <span>{t('overview.contribution.consistencyLabel')}</span>
            <TennisBall size={13} strokeWidth={1.8} />
          </div>
          {/* Winners are charted below (ShotRaces), as in In Depth. */}
          <ConsistencyDiverging data={consData} hideFoot hideWinners />
        </div>
      ) : null}

      {/* Error rate, winner rate and winners per error, each run shot by shot
          for all four players (like the Matchlytics Score), with where they
          finished. */}
      <ShotRaces
        segment={shotSegment}
        playerIds={racePlayers}
        colors={colors}
        crops={crops}
        focusPlayerId={focusPlayerId}
        tour="shot-races"
      />

      {/* How you lost your points — net vs wall errors. */}
      {loss && lossPcts ? (
        <div className="contribution-section">
          <div className="contribution-q">
            {t('overview.contribution.howLost')}
          </div>
          <div className="loss-gauge">
            <div className="loss-gauge-figure">
              <GaugeArc
                segments={[
                  { key: 'net', pct: lossPcts[0], color: LOSS_COLORS.net },
                  { key: 'wall', pct: lossPcts[1], color: LOSS_COLORS.wall },
                ]}
              />
              {/* Red X in the bowl — the symbol for errors / lost points. */}
              <span className="loss-gauge-mark" aria-hidden="true">
                <svg viewBox="0 0 16 16" width="24" height="24">
                  <line
                    x1="3.5"
                    y1="3.5"
                    x2="12.5"
                    y2="12.5"
                    stroke="var(--red)"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                  />
                  <line
                    x1="3.5"
                    y1="12.5"
                    x2="12.5"
                    y2="3.5"
                    stroke="var(--red)"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </div>
            <ul className="loss-legend">
              <li className="position-depth-item">
                <span
                  className="position-depth-dot"
                  style={{ background: LOSS_COLORS.net }}
                />
                <span className="position-depth-pct">{lossPcts[0]}%</span>
                <span className="position-depth-name">
                  {t('overview.contribution.net')}
                </span>
              </li>
              <li className="position-depth-item">
                <span
                  className="position-depth-dot"
                  style={{ background: LOSS_COLORS.wall }}
                />
                <span className="position-depth-pct">{lossPcts[1]}%</span>
                <span className="position-depth-name">
                  {t('overview.contribution.wall')}
                </span>
              </li>
            </ul>
          </div>
        </div>
      ) : null}

      {/* Shot distribution per player — your team vs the opponents. */}
      {showDistribution && roster ? (
        <div className="contribution-section">
          <Eyebrow>{t('overview.contribution.shotDist')}</Eyebrow>
          <ShotSplitBar
            sectionLabel={t('overview.position.yourTeam')}
            left={player(yourByRole[0])}
            right={player(yourByRole[1])}
          />
          <ShotSplitBar
            sectionLabel={tl('opponents')}
            left={player(oppByRole[0])}
            right={player(oppByRole[1])}
          />
        </div>
      ) : null}
    </div>
  );
};

export default ContributionCard;
